import uniqueId from 'lodash/uniqueId';
import { z } from 'zod';
import type { SymbolKey } from '@/entities/symbol';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import {
  balancedPalette,
  chartPaletteSelection,
  ColorPalette,
  monoBluePalette,
  vibrantPalette
} from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import type {
  BasicTableValues,
  ChartFormValues,
  SpotHistoryTableValues,
  SymbolsState
} from '@/features/Dashboard/form/AppForm/context/types';
import type {
  FWDChartWidgetV2,
  MarketApp,
  SeasonalChartWidgetV2,
  SimpleChartWidgetV2,
  SpotHistoryTableItem,
  SpotHistoryTableWidgetV2,
  TableRow,
  TableWidgetV2,
  TileBigNumber
} from '@/features/Dashboard/types';
import { AppType } from '@/features/Dashboard/types';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';
import type { FWDTemplateChartSeries, TemplateChartSeries } from '@/types/charts';

type SymbolKeyLike =
  | Partial<Pick<SymbolKey, 'Code' | 'PeriodType' | 'Period' | 'TimeRef'>>
  | null
  | undefined;
type SeriesSource = TemplateChartSeries | FWDTemplateChartSeries | TileBigNumber | TableRow;
type PaletteColor = { colorHex: string; colorName: string };

export const SymbolKeySchema = z.object({
  Code: z.string(),
  PeriodType: z.string(),
  Period: z.string(),
  TimeRef: z.string(),
  transactionDate: z.string().optional()
});

export const TabSymbolSchema = z.object({
  id: z.string(),
  shortTitle: z.string().min(1, 'Short Title is required'),
  symbol: SymbolKeySchema,
  group: z.string()
});

export const SymbolsStateSchema = z.object({
  active: z.array(TabSymbolSchema).min(1, 'At least one item is required'),
  backlog: z.array(TabSymbolSchema)
});

export const ColorPaletteSchema = z.enum(ColorPalette);
export const NameSchema = createNameSchema('Tab name', TABNAME_MAX_LENGTH);

const normalizeSymbolKey = (key: SymbolKeyLike): SymbolKey => ({
  Code: key?.Code ?? '',
  PeriodType: key?.PeriodType ?? '',
  Period: key?.Period ?? '',
  TimeRef: key?.TimeRef ?? ''
});

export const buildId = (key: SymbolKeyLike): string => {
  const symbol = normalizeSymbolKey(key);
  return `${symbol.Code}-${symbol.PeriodType}-${symbol.Period}-${symbol.TimeRef}`;
};

export const seriesToSymbols = (series: readonly SeriesSource[] = []): SymbolsState['active'] =>
  series.map(item => {
    const symbol = normalizeSymbolKey(item.symbol.key);

    return {
      id: buildId(symbol),
      shortTitle: item.shortTitle ?? symbol.Code,
      symbol,
      group: item.symbol.group
    };
  });

export const findColorPalette = (
  series: readonly (TemplateChartSeries | FWDTemplateChartSeries)[] | undefined
): ColorPalette => {
  const color = series?.[0]?.settings?.color;
  switch (color) {
    case vibrantPalette[0].hex:
      return ColorPalette.Vibrant;
    case monoBluePalette[0].hex:
      return ColorPalette.Monochrome;
    case balancedPalette[0].hex:
      return ColorPalette.Balanced;
    default:
      return ColorPalette.Vibrant;
  }
};

const getPaletteColor = (colorPalette: ColorPalette, index: number): PaletteColor => {
  const palette = chartPaletteSelection.find(color => color.id === colorPalette);
  const paletteColors = palette?.palette ?? [];
  const paletteIndex = paletteColors.length > 0 ? index % paletteColors.length : 0;

  return {
    colorHex: paletteColors[paletteIndex]?.hex ?? '#000000',
    colorName: paletteColors[paletteIndex]?.name ?? 'default'
  };
};

const buildTemplateSeries = (
  symbols: SymbolsState,
  colorPalette: ColorPalette
): TemplateChartSeries[] =>
  symbols.active.map((symbol, index) => {
    const { colorHex, colorName } = getPaletteColor(colorPalette, index);

    return {
      settings: { type: 'line', color: colorHex, colorName },
      shortTitle: symbol.shortTitle,
      symbol: {
        key: normalizeSymbolKey(symbol.symbol),
        group: 'Prod_Indexes',
        columns: null
      }
    };
  });

const buildFwdSeries = (
  symbols: SymbolsState,
  colorPalette: ColorPalette
): FWDTemplateChartSeries[] =>
  symbols.active.map((symbol, index) => {
    const { colorHex, colorName } = getPaletteColor(colorPalette, index);

    return {
      settings: { type: 'line', color: colorHex, colorName },
      shortTitle: symbol.shortTitle,
      symbol: {
        key: {
          Code: symbol.symbol.Code,
          PeriodType: symbol.symbol.PeriodType,
          TimeRef: symbol.symbol.TimeRef
        },
        group: 'Prod_Indexes',
        columns: null
      }
    };
  });

const toSeasonalMetadataFrom = (from: ChartFormValues['from']) => {
  const value = from == null ? '' : String(from).trim();
  if (!value) return '2y';
  return value.endsWith('y') ? value : `${value}y`;
};

const fromSeasonalMetadata = (from: string | undefined) => from?.replace(/y$/, '') ?? '2';

export const buildChart = (values: ChartFormValues): MarketApp => {
  const tabId = values.id ?? uniqueId(new Date().toISOString());

  if (values.type === AppType.Seasonal) {
    const tab: SeasonalChartWidgetV2 = {
      id: tabId,
      name: values.name,
      metadata: { graphType: 'seasonal', from: toSeasonalMetadataFrom(values.from) },
      series: buildTemplateSeries(values.symbols, values.colorPalette),
      type: 'graph'
    };

    return { id: uniqueId(new Date().toISOString()), tabs: [tab] };
  }

  if (values.type === AppType.FWD) {
    const tab: FWDChartWidgetV2 = {
      id: tabId,
      name: values.name,
      metadata: { graphType: 'forwardSimple' },
      series: buildFwdSeries(values.symbols, values.colorPalette),
      type: 'graph'
    };

    return { id: uniqueId(new Date().toISOString()), tabs: [tab] };
  }

  const tab: SimpleChartWidgetV2 = {
    id: tabId,
    name: values.name,
    metadata: null,
    series: buildTemplateSeries(values.symbols, values.colorPalette),
    type: 'graph'
  };

  return { id: uniqueId(new Date().toISOString()), tabs: [tab] };
};

export const buildTable = (
  name: string,
  symbols: SymbolsState,
  metadata: TableWidgetV2['metadata'],
  tabId?: string
): MarketApp => {
  const rows: TableRow[] = symbols.active.map(symbol => ({
    shortTitle: symbol.shortTitle,
    symbol: { key: normalizeSymbolKey(symbol.symbol), group: symbol.group },
    diffSymbol: {
      group: symbol.group,
      key: { Code: '', PeriodType: '', Period: '', TimeRef: '' }
    }
  }));

  const tab: TableWidgetV2 = {
    id: tabId ?? uniqueId(new Date().toISOString()),
    name,
    layout: null,
    metadata,
    type: 'table',
    sections: [{ name: '', rows }]
  };

  return { id: uniqueId(new Date().toISOString()), tabs: [tab] };
};

export const buildSpotHistoryTable = (
  name: string,
  symbols: SpotHistoryTableValues['symbols'],
  tabId?: string
): MarketApp => {
  const items: SpotHistoryTableItem[] = symbols.active.map(symbol => ({
    name: symbol.shortTitle,
    symbol: { key: normalizeSymbolKey(symbol.symbol), group: symbol.group }
  }));

  const tab: SpotHistoryTableWidgetV2 = {
    id: tabId ?? uniqueId(new Date().toISOString()),
    name,
    layout: null,
    metadata: null,
    type: 'spot_history_table',
    items
  };

  return { id: uniqueId(new Date().toISOString()), tabs: [tab] };
};

export const graphToFormValues = (
  widget: SimpleChartWidgetV2 | SeasonalChartWidgetV2 | FWDChartWidgetV2
): ChartFormValues => {
  const graphType = widget.metadata?.graphType;
  let type: AppType.Spot | AppType.Seasonal | AppType.FWD = AppType.Spot;

  if (graphType === 'seasonal') {
    type = AppType.Seasonal;
  } else if (graphType === 'forwardSimple') {
    type = AppType.FWD;
  }

  return {
    id: widget.id,
    name: widget.name,
    type,
    symbols: { active: seriesToSymbols(widget.series), backlog: [] },
    colorPalette: findColorPalette(widget.series),
    from: graphType === 'seasonal' ? fromSeasonalMetadata(widget.metadata?.from) : undefined
  };
};

export function tableToFormValues(
  widget: SpotHistoryTableWidgetV2,
  type: AppType.SpotHistoryTable
): SpotHistoryTableValues;
export function tableToFormValues(widget: TableWidgetV2, type: AppType.Table): BasicTableValues;
export function tableToFormValues(
  widget: TableWidgetV2 | SpotHistoryTableWidgetV2,
  type: AppType.Table | AppType.SpotHistoryTable
): BasicTableValues | SpotHistoryTableValues {
  if (type === AppType.SpotHistoryTable) {
    const spotHistoryWidget = widget as SpotHistoryTableWidgetV2;

    return {
      id: spotHistoryWidget.id,
      type,
      name: spotHistoryWidget.name,
      symbols: {
        active: spotHistoryWidget.items.map(item => ({
          id: buildId(item.symbol.key),
          shortTitle: item.name,
          symbol: normalizeSymbolKey(item.symbol.key),
          group: item.symbol.group
        })),
        backlog: []
      }
    };
  }

  const tableWidget = widget as TableWidgetV2;

  return {
    id: tableWidget.id,
    type,
    name: tableWidget.name,
    symbols: {
      active: seriesToSymbols(tableWidget.sections.flatMap(section => section.rows)),
      backlog: []
    }
  };
}
